import { Input } from "components/Input/Input";
import { Select } from "components/Select/Select";
import { CURSADO_OPTIONS } from "components/EspaciosTable/EspaciosTable";
import { set } from "date-fns";
import { useFormik } from "formik";
import { CreateCajaCurricularPciI } from "interfaces/CreateCajaCurricularPci";
import { CreateEspacioCurricularPciI } from "interfaces/CreateEspacioCurricularPci";
import { useEffect, useState } from "react";
import { Button, Col, Modal, Row } from "react-bootstrap";
import { getArea } from "services/area";
import { getEspaciosCurricularesByCajaCurricular, getEspaciosCurricularesByPlanEstudio } from "services/espacioCurricular";
import { getTipoEspacio } from "services/tipoEspacio";
import { createEspacioCurricularPciSchema } from "schemas/pci/createEspacioCurricularPciSchema";

interface Props {
  actionType: "create" | "edit" | undefined;
  onClose?: any;
  elemento?: CreateEspacioCurricularPciI;
  onSuccess?: (
    espacioCurricularPci: CreateEspacioCurricularPciI,
    cajaIndex: number,
  ) => void;
  showModal: boolean;
  cajaCurricularPciList: CreateCajaCurricularPciI[];
  defaultCajaIndex?: string;
  planEstudioId?: number | string;
  usedEspacioBaseIds?: (number | string)[];
}

interface initialValuesI {
  nombre: string;
  carga_horaria: number | string;
  cuatrimestre: number | string; // "" = anual
  detalle_modificacion: string;
  anio: number | string;
  area_id: number | string;
  tipo_espacio_id: number | string;
}

export const EspacioCurricularPciABM = ({
  showModal,
  actionType,
  onClose,
  elemento,
  onSuccess,
  cajaCurricularPciList,
  defaultCajaIndex,
  planEstudioId,
  usedEspacioBaseIds = [],
}: Props) => {
  const isEditMode = actionType === "edit";
  const [areas, setAreas] = useState<any[]>([]);
  const [tiposEspacio, setTiposEspacio] = useState<any[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [espacioCurricular, setEspacioCurricular] = useState<string>("");
  const [selectedCajaIndex, setSelectedCajaIndex] = useState<string>("");
  const [espacioCurricularOptions, setEspacioCurricularOptions] = useState<any[]>([]);
  const [isLoadingEspacios, setIsLoadingEspacios] = useState(false);

  // Opciones de cajas curriculares disponibles (las ya creadas en el PCI)
  const cajaCurricularOptions = cajaCurricularPciList.map((caja, index) => ({
    id: String(index),
    nombre: `${caja.descripcion} (v${caja.version})`,
  }));

  const initialValues: initialValuesI = {
    nombre: "",
    carga_horaria: "",
    cuatrimestre: "",
    detalle_modificacion: "",
    anio: "",
    area_id: "",
    tipo_espacio_id: "",
  };

  const loadSelectData = async () => {
    try {
      setIsLoadingData(true);
      const [areasRes, tiposRes] = await Promise.all([
        // Fallaba el autocompletado asi que se aumento el limite de items para que no haya errores, en caso de que haya mas de 1000, se debera implementar un endpoint especifico para traer solo los necesarios.
        getArea({limit: 1000}),
        getTipoEspacio({}),
      ]);
      setAreas(areasRes?.elementos ?? areasRes ?? []);
      setTiposEspacio(tiposRes?.elementos ?? tiposRes ?? []);
      
    } catch (err) {
      console.log("Error cargando datos para selects", err);
    } finally {
      setIsLoadingData(false);
    }
  };

  const renderInitialValues = async () => {
    let currentValues;
    
    if (isEditMode && elemento) {
      currentValues = {
        nombre: elemento?.nombre ?? "",
        carga_horaria: elemento?.carga_horaria ?? "",
        cuatrimestre: elemento?.cuatrimestre ?? "",
        detalle_modificacion: elemento?.detalle_modificacion ?? "",
        anio: elemento?.anio ?? "",
        area_id: elemento?.area_id ?? "",
        tipo_espacio_id: elemento?.tipo_espacio_id ?? ""
      };
    } else {
      currentValues = initialValues;
    }
    setValues(currentValues);
  };

  const handleEspacioCurricularChange = (e: any) => {
    const selectedId = e.target.value;
    setEspacioCurricular(selectedId);

    if (!selectedId) return;

    const selectedEspacio = espacioCurricularOptions.find(
      (ec: any) => String(ec.id) === String(selectedId),
    );
    
    if (selectedEspacio) {
      setValues({
        ...values,
        nombre: selectedEspacio.nombre ?? "",
        carga_horaria: selectedEspacio.horas_semanales ?? "",
        cuatrimestre: selectedEspacio.cuatrimestre ?? "",
        anio: selectedEspacio.anio ?? "",
        area_id: selectedEspacio.area?.id ?? "",
        tipo_espacio_id: selectedEspacio.tipo_espacio ? selectedEspacio.tipo_espacio?.id : selectedEspacio.tipoEspacio.id,
      });
    }
  };

  const handleSubmitForm = async (params: initialValuesI) => {
    if (selectedCajaIndex === "") return;

    // Resolver nombres para display en la tabla
    const selectedArea = areas.find(
      (a: any) => String(a.id) === String(params.area_id)
    );
    const selectedTipoEspacio = tiposEspacio.find(
      (t: any) => String(t.id) === String(params.tipo_espacio_id)
    );

    const newEspacio: CreateEspacioCurricularPciI = {
      ...(elemento?.id ? { id: elemento.id } : {}),
      espacio_curricular_id: espacioCurricular || undefined,
      nombre: params.nombre,
      carga_horaria: params.carga_horaria,
      // "" (anual) se normaliza a null para que el estado coincida con lo que devuelve la API
      cuatrimestre: params.cuatrimestre === "" ? null : Number(params.cuatrimestre),
      detalle_modificacion: params.detalle_modificacion,
      anio: params.anio,
      area_id: params.area_id,
      tipo_espacio_id: params.tipo_espacio_id,
      area_nombre: selectedArea?.nombre ?? "",
      tipo_espacio_nombre: selectedTipoEspacio?.nombre ?? "",
    } as any;
    if (onSuccess) onSuccess(newEspacio, Number(selectedCajaIndex));
    
    onClose();
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    isSubmitting,
    handleSubmit,
    setValues,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema: createEspacioCurricularPciSchema,
    onSubmit: handleSubmitForm,
  });

  useEffect(() => {
    resetForm();
    setSelectedCajaIndex(defaultCajaIndex ?? "");
    
    if (actionType === "edit" && elemento?.espacio_curricular_id) {
      setEspacioCurricular(String(elemento.espacio_curricular_id));
    } else {
      setEspacioCurricular("");
    }
    if (actionType) {
      renderInitialValues();
      loadSelectData();
    }
  }, [actionType]);

  // Fetch espacios curriculares when the selected caja changes
  useEffect(() => {
    if (selectedCajaIndex === "") {
      setEspacioCurricularOptions([]);
      setEspacioCurricular("");
      return;
    }

    const cajaSelected = cajaCurricularPciList[Number(selectedCajaIndex)];
    if (!cajaSelected?.caja_curricular_id) {
      setEspacioCurricularOptions([]);
      setEspacioCurricular("");
      return;
    }

    const fetchEspacios = async () => {
      try {
        setIsLoadingEspacios(true);
        if (!isEditMode) {
          setEspacioCurricular("");
        }
        const res = await getEspaciosCurricularesByCajaCurricular({
          caja_curricular_id: cajaSelected.caja_curricular_id,
        });
        const opciones = res?.elementos ?? [];

        // Filtrar los espacios base que ya están siendo utilizados por espacios PCI existentes.
        // En modo edición, se excluye el ID del espacio actual para que siga apareciendo.
        const currentEditingBaseId = isEditMode ? elemento?.espacio_curricular_id : undefined;
        const filteredOpciones = opciones.filter((ec: any) => {
          // Si es el espacio que estamos editando, no filtrar
          if (currentEditingBaseId && String(ec.id) === String(currentEditingBaseId)) {
            return true;
          }
          // Si el ID ya está en uso, excluirlo
          return !usedEspacioBaseIds.some((usedId) => String(usedId) === String(ec.id));
        });

        setEspacioCurricularOptions(filteredOpciones);
      } catch (err) {
        console.log("Error cargando espacios curriculares", err);
        setEspacioCurricularOptions([]);
      } finally {
        setIsLoadingEspacios(false);
      }
    };

    fetchEspacios();
  }, [selectedCajaIndex]);

  return (
    <>
      <Modal show={showModal} onHide={onClose} size="lg" centered>
        <Modal.Header>
          <Modal.Title>
            {isEditMode
              ? "Editar Espacio Curricular PCI"
              : "Nuevo Espacio Curricular PCI"}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <Col>
              <Row>
                <Col sm={12} className="mb-3">
                  <Select
                    id="caja_curricular_destino"
                    title="Caja Curricular PCI destino"
                    size="lg"
                    optionsFormatConfig={{ value: "id", label: "nombre" }}
                    options={cajaCurricularOptions}
                    placeholder="Seleccionar caja curricular"
                    variant="gray-container"
                    required
                    values={{
                      caja_curricular_destino: selectedCajaIndex,
                    }}
                    errors={{} as any}
                    touched={{} as any}
                    onChange={(e: any) =>
                      setSelectedCajaIndex(e.target.value)
                    }
                    onBlur={() => {}}
                  />
                </Col>
                <Col sm={12} className="mb-3">
                  <Select
                    id="espacio_curricular"
                    title="Espacio Curricular"
                    size="lg"
                    optionsFormatConfig={{ value: "id", label: "nombre" }}
                    options={espacioCurricularOptions}
                    isLoading={isLoadingEspacios}
                    placeholder="Seleccionar"
                    variant="gray-container"
                    values={{ espacio_curricular: espacioCurricular }}
                    errors={{} as any}
                    touched={{} as any}
                    onChange={handleEspacioCurricularChange}
                    onBlur={() => {}}
                    disabled={selectedCajaIndex === ""}
                  />
                </Col>
                <Col sm={6} className="mb-3">
                  <Input
                    id={"nombre"}
                    title="Nombre"
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                  />
                </Col>
                <Col sm={3} className="mb-3">
                  <Input
                    id={"anio"}
                    title="Año"
                    type="number"
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                    min={1}
                  />
                </Col>
                <Col sm={3} className="mb-3">
                  <Input
                    id={"carga_horaria"}
                    title="Carga horaria"
                    type="number"
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                    min={1}
                  />
                </Col>
                <Col sm={4} className="mb-3">
                  <Select
                    id="cuatrimestre"
                    title="Cursado"
                    size="lg"
                    options={CURSADO_OPTIONS}
                    variant="gray-container"
                    required
                    values={values}
                    errors={errors}
                    touched={touched}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isSearchable={false}
                  />
                </Col>
                <Col sm={4} className="mb-3">
                  <Select
                    id="area_id"
                    title="Área"
                    size="lg"
                    optionsFormatConfig={{ value: "id", label: "nombre" }}
                    options={areas}
                    isLoading={isLoadingData}
                    placeholder="Seleccionar"
                    variant="gray-container"
                    required
                    values={values}
                    errors={errors}
                    touched={touched}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    // TODO: Consultar si este campo es editable o no.
                    // disabled={isEditMode}
                    
                  />
                </Col>
                <Col sm={4} className="mb-3">
                  <Select
                    id="tipo_espacio_id"
                    title="Tipo espacio"
                    size="lg"
                    optionsFormatConfig={{ value: "id", label: "nombre" }}
                    options={tiposEspacio}
                    isLoading={isLoadingData}
                    placeholder="Seleccionar"
                    variant="gray-container"
                    required
                    values={values}
                    errors={errors}
                    touched={touched}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    // TODO: Consultar si este campo es editable o no.
                    // disabled={isEditMode}
                  />
                </Col>
                <Col sm={12} className="mb-3">
                  <Input
                    id={"detalle_modificacion"}
                    title="Detalle de modificación"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    values={values}
                    errors={errors}
                    touched={touched}
                    variant="gray-container"
                    size="lg"
                    type="textarea"
                    maxLength={500}
                    defaultHeight={"80px"}
                  />
                </Col>
              </Row>
            </Col>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="mx-3"
              variant="secondary"
              size="lg"
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="lg"
              className="user-submit-button"
              type="submit"
              disabled={selectedCajaIndex === ""}
            >
              {isEditMode ? "Guardar Cambios" : "Confirmar"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};
